import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { DocumentClassificationStatus } from "documents/classification/document-classification-status";

const StateChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(0.75),
}));

/**
 * What a chip says for one state: the words on screen, the explanation behind them, and how
 * alarming it should look.
 *
 * The assistant says the same things about the same document - see
 * `AssistantSourcePrompt.Explain` in `api/AIAssistant` - and a user who reads one and then the
 * other should not be told two different stories. The substance has to match; the phrasing here
 * is shorter because a chip is not a sentence.
 *
 * Every explanation leads with what the reader can do, because that is the only reason to
 * separate the states at all: waiting resolves the first, re-uploading resolves the second, and
 * neither does anything for the third.
 *
 * `Unindexable` is the one that is not a problem, and it is the one most easily written as if it
 * were. Nothing failed, nothing is withheld, and the document downloads exactly as it always
 * did - a scan simply has no text in it to search. Saying "processing failed, try uploading it
 * again" here sends a user to re-upload the same scan and watch it happen again, which is the
 * defect this state exists to end (#5192).
 */
interface ChipCopy {
  label: string;
  explanation: string;
  color: "default" | "warning";
}

const explanations: Record<DocumentClassificationStatus, ChipCopy | null> = {
  [DocumentClassificationStatus.Pending]: {
    label: "Processing",
    color: "default",
    explanation:
      "Still being processed. This usually takes under a minute, and it becomes available on "
      + "its own - you do not need to upload it again.",
  },
  [DocumentClassificationStatus.Failed]: {
    label: "Processing failed",
    color: "warning",
    explanation:
      "Processing did not succeed, so this document cannot be searched or used to answer "
      + "questions, and its contents are withheld until it is processed again. Waiting will not "
      + "change that; uploading it again is the thing most likely to help.",
  },
  // A processed document is what every document is expected to be, so a chip here would
  // appear on every row and say nothing.
  [DocumentClassificationStatus.Succeeded]: null,
  [DocumentClassificationStatus.Unindexable]: {
    label: "No text to search",
    color: "default",
    explanation:
      "No text could be read out of this file, which usually means it is a scan or a photo. You "
      + "can open and download it as normal - it just cannot be searched or used to answer "
      + "questions. Uploading the same file again will not change that; a copy that contains "
      + "real text, or one run through OCR, would be searchable.",
  },
};

interface DocumentProcessingStateProps {
  status: DocumentClassificationStatus | undefined;
}

/**
 * Says why a document is not answerable, or nothing at all.
 *
 * Renders for exactly three states. A processed document is what every document is expected to
 * be, so annotating it would put a chip on every row and say nothing; a document whose status
 * the payload never carried has nothing truthful to show. Both render as absent.
 *
 * `needsExplaining` is the same rule stated for callers that need it without rendering -
 * `Document.needsProcessingExplained` reads it. The two are separate and must not disagree,
 * which the spec asserts for every member of the enum.
 */
export default function DocumentProcessingState({ status }: DocumentProcessingStateProps) {
  // The table is the gate, rather than a predicate beside it. A row per status means a new
  // member of the enum will not compile until it has been given words or an explicit null, and
  // `undefined` - a payload that never carried the field - is the one case the table cannot
  // hold a row for.
  const state = status === undefined ? null : explanations[status];
  if (!state) return null;

  return (
    // describeChild, so the chip keeps its own visible text as its accessible name and the
    // explanation arrives as a description. The default would use the explanation as the name
    // instead, which would leave the accessible name without the words on screen.
    <Tooltip describeChild title={state.explanation}>
      <StateChip
        // The chip is not a button and has nothing to activate, so nothing here puts it in the
        // tab order on its own - and MUI's tooltip opens on focus, which is the only way a
        // keyboard reaches it. Without this the explanation is hover-only, and the explanation
        // is the whole point: it is where "you do not need to upload it again" is said, and
        // where a scan is told apart from a document nobody can open.
        tabIndex={0}
        size="small"
        variant="outlined"
        color={state.color}
        label={state.label}
      />
    </Tooltip>
  );
}
